import _ from 'lodash';
import moment from 'moment';
import { formatAssetSelectionFromApiOutput, formatDateRangeFromApiOutput } from '@/annotation/reportContent.utilities';
import { updateAssetSelection, updateDateRange } from '@/reportEditor/report.actions';
import { getAssetSiblings } from '@/utilities/formula.utilities';
import type Utilities from '@/core/utilities';
import type { AssetSelection, DateRange } from '@/reportEditor/report.constants';
import type { ContentWithMetadataListOutputV1 } from '@/sdk/model/ContentWithMetadataListOutputV1';
import type { ItemPreviewWithAssetsV1 } from '@/sdk/model/ItemPreviewWithAssetsV1';

enum SandboxReload {
  FORCE_RELOAD,
  NO_FORCE_RELOAD,
}

type ParamKeyValMap = Record<string, string>;

function sandboxUrlParams(
  originalData: { assetSelections: AssetSelection[]; dateRanges: DateRange[] },
  { utilities }: { utilities: typeof Utilities },
) {
  /**
   * This function will check for any URL parameters that can change the stores. The params that we're following now
   * are assetSelection, dateRangeStart and dateRangeEnd. Each change for an assetSelection or a dateRange will
   * result in a promise that returns FORCE_RELOAD. Each assetSelection or dateRange that doesn't have a change
   * will return NO_FORCE_RELOAD
   *
   * This will be used as a callback in the loadReport function as a transform method after the data loaded
   *
   * Returns FORCE_RELOAD if document and content need to be re-fetched.
   * @param urlParams The URL Parameters that were received
   * @returns Promise<boolean> - True if data needs to be reloaded
   */
  function applyUrlParamsToData(
    urlParams: URLSearchParams,
  ): (contentMetadata: ContentWithMetadataListOutputV1) => Promise<boolean> {
    return async (contentMetadata) => {
      const allChangesPromises: Promise<SandboxReload>[] = [];
      if (urlParams.get('assetSelection')) {
        const assetSelectionOverrides = utilities.splitParamToObjectValue(urlParams.getAll('assetSelection'));
        // parse each assetSelection
        _.forEach(contentMetadata.assetSelections, (singleAssetSelection) => {
          const formattedAssetSelection = formatAssetSelectionFromApiOutput(singleAssetSelection);
          allChangesPromises.push(changeAssetSelection(formattedAssetSelection, assetSelectionOverrides));
        });
      }
      if (urlParams.get('dateRangeStart') || urlParams.get('dateRangeEnd')) {
        const dateRangeStartOverrides = utilities.splitParamToObjectValue(urlParams.getAll('dateRangeStart'));
        const dateRangeEndOverrides = utilities.splitParamToObjectValue(urlParams.getAll('dateRangeEnd'));
        // parse each dateRange
        _.forEach(contentMetadata.dateRanges, (singleDateRange) => {
          const formattedDateRange = formatDateRangeFromApiOutput(singleDateRange);
          allChangesPromises.push(changeDateRange(formattedDateRange, dateRangeStartOverrides, dateRangeEndOverrides));
        });
      }
      const allResults = await Promise.all(allChangesPromises);
      return _.indexOf(allResults, SandboxReload.FORCE_RELOAD) !== -1;
    };
  }

  /**
   * This function will search for the new dateRange name in the original list and apply any changes for the
   * start / end time if received via UrlParams. If there's any change that we apply then we will return a FORCE_RELOAD
   * value that will tell the loading engine to reload all the data since there are changes that were applied
   *
   * @param dateRange New DateRange to be changed
   * @param startOverrides Overrides received via UrlParams for start time
   * @param endOverrides Overrides received via UrlParams for end time
   * @returns
   */
  async function changeDateRange(
    dateRange: DateRange,
    startOverrides: ParamKeyValMap,
    endOverrides: ParamKeyValMap,
  ): Promise<SandboxReload> {
    const oldDateRange = _.find(originalData.dateRanges, ['name', dateRange.name])!;
    const clonedDateRange = _.cloneDeep(dateRange);
    const startTime = moment(_.get(startOverrides, oldDateRange.id, -1), moment.ISO_8601);
    const endTime = moment(_.get(endOverrides, oldDateRange.id, -1), moment.ISO_8601);
    if (startTime.isValid()) {
      _.set(clonedDateRange, 'range.start', _.toNumber(startTime.format('x')));
    }
    if (endTime.isValid()) {
      _.set(clonedDateRange, 'range.end', _.toNumber(endTime.format('x')));
    }
    if (startTime.isValid() || endTime.isValid()) {
      await updateDateRange(clonedDateRange);
      return SandboxReload.FORCE_RELOAD;
    }
    return SandboxReload.NO_FORCE_RELOAD;
  }

  /**
   * This function will search for the new AssetSelection name in the original list and apply any changes for the
   * asset if received via UrlParams. If there's any change that we apply then we will return a FORCE_RELOAD
   * value that will tell the loading engine to reload all the data since there are changes that were applied
   *
   * @param assetSelection New AssetSelection to be changed
   * @param assetSelectionOverrides Overrides received via UrlParams for the assetSelections
   * @returns
   */
  async function changeAssetSelection(
    assetSelection: AssetSelection,
    assetSelectionOverrides: ParamKeyValMap,
  ): Promise<SandboxReload> {
    // check if we need to change this asset selection
    const oldSelection = _.find(originalData.assetSelections, ['name', assetSelection.name])!;
    const newAssetId = _.get(assetSelectionOverrides, oldSelection.id ?? '', false);
    if (oldSelection && newAssetId) {
      const siblings = await getAssetSiblings(assetSelection);
      const newAsset = _.find(siblings, ['id', newAssetId]) as ItemPreviewWithAssetsV1;
      if (newAsset) {
        assetSelection.asset = newAsset;

        await updateAssetSelection(assetSelection);
        return SandboxReload.FORCE_RELOAD;
      }
      return SandboxReload.NO_FORCE_RELOAD;
    }

    return SandboxReload.NO_FORCE_RELOAD;
  }

  return {
    applyUrlParamsToData,
  };
}

export { sandboxUrlParams, SandboxReload };
